import { AssistantMessageSchema, type DtUIMessage } from '@ditto/shared';
import { DeepPartial, parsePartialJson } from 'ai';
import { useEffect, useState } from 'react';
import { z } from 'zod/v4';

const AssistantMessageUpdateSchema = AssistantMessageSchema.partial({
  content: true,
}).extend({
  citations: AssistantMessageSchema.shape.citations.optional(),
});

type AssistantMessageUpdate = z.infer<typeof AssistantMessageUpdateSchema>;

/** Parsed message object with the original message id */
export type ParsedAssistantMessage = DeepPartial<AssistantMessageUpdate> & {
  messageId: string;
};

/**
 * Transform a single message to a typed message object with its id.
 * This is a pure async function, not a hook.
 * Handles both new JSON format and legacy plain text messages.
 */
const transformMessage = async (
  msg: DtUIMessage
): Promise<ParsedAssistantMessage | null> => {
  const text = msg?.parts.find(part => part.type === 'text')?.text ?? '';

  try {
    // Try parsing as JSON first (new format)
    const { value } = await parsePartialJson(text);

    // If valid object with content, use structured format
    if (value && typeof value === 'object' && 'content' in value) {
      return {
        ...(value as DeepPartial<AssistantMessageUpdate>),
        messageId: msg.id,
      };
    }

    // Fallback: treat raw text as content (legacy plain text messages)
    if (text) {
      return {
        content: text,
        citations: [],
        messageId: msg.id,
      };
    }

    return null;
  } catch (e) {
    // Even on parse error, show raw text if available (legacy support)
    if (text) {
      return {
        content: text,
        citations: [],
        messageId: msg.id,
      };
    }
    console.error('Message parsing error:', e);
    return null;
  }
};

/**
 * Hook to parse DtUIMessages into structured typed objects.
 * Handles the entire array at once to comply with React's Rules of Hooks.
 * Each parsed message includes the original message id.
 */
export const useParsedMessages = (
  messages: DtUIMessage[]
): ParsedAssistantMessage[] => {
  const [parsedMessages, setParsedMessages] = useState<
    (ParsedAssistantMessage | null)[]
  >([]);

  useEffect(() => {
    let isMounted = true;

    const loadAllMessages = async () => {
      const results = await Promise.all(
        messages.map(message => transformMessage(message))
      );
      if (isMounted) {
        setParsedMessages(results);
      }
    };

    loadAllMessages();

    return () => {
      isMounted = false;
    };
  }, [messages]);

  // Filter out nulls/undefined and return only valid parsed messages
  return parsedMessages.filter(
    (obj): obj is ParsedAssistantMessage => obj != null
  );
};
